import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

import Loaders from "components/Common/Loaders";
import { convertToDateAndTime } from "helpers/GeneralHelper";
import { invokeCandidateTestActionForCallDetails } from "model/CallModel";
import CallDetailsCandidateTestUpdateModal from "pages/CallDetails/callDetailsCandidateTestUpdateModal";
import CallDetailsRoadTestScheduleUpdateModal from "pages/CallDetails/callDetailsRoadTestScheduleUpdateModal";
import CallDetailsScreeningMailPreviewModal from "pages/CallDetails/callDetailsScreeningMailPreviewModal";

import {
    buildCandidateTestActionRequestBody,
    doesCandidateTestActionLabelOpenDriverDetailsDocumentModal,
    doesCandidateTestActionLabelRequireSendLinkConfirmation,
    doesCandidateTestActionLabelOpenUpdateFormModal,
    doesCandidateTestRowAndLabelOpenRoadTestScheduleChangeModal,
    resolveCandidateApplicationStatusTestBadgeClassNames,
    resolveCandidateApplicationStatusTestBadgeLabel,
    resolveCandidateApplicationStatusTestTitle,
    resolveCandidateTestFadvFileDispatch,
    resolveCandidateTestFadvFileDispatchChipClassNames,
    resolveCandidateTestFadvFileDispatchStatusLabel,
    resolveCandidateTestRowBackgroundClassNames,
    doesCandidateTestActionOpenVideoInterviewCompletedLink,
    resolveVideoInterviewCompletedLink,
    shouldRenderVideoInterviewCompletedViewButton,
    sortCandidateApplicationStatusTestsByServerSortOrder,
} from "helpers/callDetailsCandidateApplicationStatusTestPresentation";

const formatFadvDispatchDisplayValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    const text = String(value).trim();
    return text || "—";
};

const formatFadvDispatchLastSentOn = (lastSentOn) => {
    if (lastSentOn === null || lastSentOn === undefined || lastSentOn === "") {
        return "—";
    }
    const formatted = convertToDateAndTime(lastSentOn);
    return formatFadvDispatchDisplayValue(formatted || lastSentOn);
};

const FadvFileDispatchDetailsModal = ({
    isOpen,
    onRequestClose,
    requirementTitle = "",
    fadvFileDispatch = null,
}) => {
    const statusLabel = resolveCandidateTestFadvFileDispatchStatusLabel(
        fadvFileDispatch || {}
    );

    return (
        <Modal
            isOpen={isOpen}
            toggle={onRequestClose}
            centered
            size="md"
            className="cd-fadv-dispatch-modal modal-full-height"
        >
            <ModalHeader toggle={onRequestClose}>
                Uploaded File details
                {requirementTitle ? (
                    <span className="cd-fadv-dispatch-modal__subtitle d-block">
                        {requirementTitle}
                    </span>
                ) : null}
            </ModalHeader>
            <ModalBody>
                <div className="cd-fadv-dispatch-modal__rows">
                    <div className="cd-fadv-dispatch-modal__row">
                        <span className="cd-fadv-dispatch-modal__label">Status</span>
                        <span className="cd-fadv-dispatch-modal__value fw-semibold">
                            {statusLabel}
                        </span>
                    </div>
                    <div className="cd-fadv-dispatch-modal__row">
                        <span className="cd-fadv-dispatch-modal__label">Last sent on</span>
                        <span className="cd-fadv-dispatch-modal__value">
                            {formatFadvDispatchLastSentOn(
                                fadvFileDispatch?.lastSentOn
                            )}
                        </span>
                    </div>
                    <div className="cd-fadv-dispatch-modal__row cd-fadv-dispatch-modal__row--message">
                        <span className="cd-fadv-dispatch-modal__label">Message</span>
                        <div className="cd-fadv-dispatch-modal__message">
                            {formatFadvDispatchDisplayValue(
                                fadvFileDispatch?.fadvApiMessage
                            )}
                        </div>
                    </div>
                    <div className="cd-fadv-dispatch-modal__row">
                        <span className="cd-fadv-dispatch-modal__label">Retry count</span>
                        <span className="cd-fadv-dispatch-modal__value">
                            {formatFadvDispatchDisplayValue(
                                fadvFileDispatch?.retryCount
                            )}
                        </span>
                    </div>
                </div>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" type="button" onClick={onRequestClose}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

const openUrlInNewTab = url => {
    const link = String(url ?? "").trim();
    if (!link) return;
    window.open(link, "_blank", "noopener,noreferrer");
};

const VideoInterviewCompletedViewButton = ({ href }) => {
    const link = String(href ?? "").trim();
    if (!link) return null;

    return (
        <Button
            type="button"
            color="primary"
            className="action-btn uniform-btn video-interview-with-icon"
            title="View interview in a new tab"
            onClick={() => openUrlInNewTab(link)}
        >
            <i className="mdi mdi-open-in-new me-1" aria-hidden="true" />
            View interview link
        </Button>
    );
};

const isSuccess = (response) =>
    Boolean(
        response?.success ||
            response?.isSuccess ||
            response?.status === "success" ||
            response?.status === true
    );

const isScreeningMailGetAction = (action) =>
    String(action?.kind ?? "").toLowerCase() === "screening-mail" &&
    String(action?.httpMethod ?? "").trim().toUpperCase() === "GET";

const resolveScreeningMailSendAction = (screeningMailResponseData) => {
    const actions = Array.isArray(screeningMailResponseData?.actions)
        ? screeningMailResponseData.actions
        : [];

    return (
        actions.find(
            (action) =>
                String(action?.kind ?? "").toLowerCase() ===
                "screening-mail-send"
        ) ?? actions[0] ?? null
    );
};

const getProgressKey = (row, action) =>
    `${row?.candidateTestId || ""}|${action?.kind || ""}|${action?.routeKey || ""}`;

const getButtonClass = (action, category = "") => {
    const kind = String(action?.kind || "").toLowerCase();
    const type = String(category).toLowerCase();

    if (type.includes("video")) {
        return `action-btn uniform-btn ${kind.includes("approve") ? 'video-approve-btn-call' : kind.includes("reject") ? 'video-reject-btn-call' : 'video-interview-with-icon'}`;
    }

    if (kind.includes("change")) {
        return "action-btn change-btn-with-icon";
    }

    if (kind.includes("mec") || kind.includes("resend")) {
        return "action-btn mec-btn-clr";
    }

    if (kind.includes("upload")) {
        return "action-btn uniform-btn upload-with-icon";
    }
    if (kind.includes("approve")) {
        return "action-btn uniform-btn approve-btn-clr-call";
    }
    if (kind.includes("reject")) {
        return "action-btn uniform-btn reject-btn-clr-call ";
    }

    return "action-btn uniform-btn";
};

const hasCandidateTestRowNote = (row) =>
    Boolean(String(row?.notes ?? "").trim());

const CandidateTestRowNoteInfo = ({ note }) => {
    const noteText = String(note ?? "").trim();
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (!isOpen) return undefined;

        const handlePointerDown = (event) => {
            if (!wrapperRef.current?.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handlePointerDown);
        return () => document.removeEventListener("mousedown", handlePointerDown);
    }, [isOpen]);

    if (!noteText) return null;

    const handleToggle = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsOpen((current) => !current);
    };

    return (
        <span
            ref={wrapperRef}
            className={[
                "cd-test-note-info-wrap",
                isOpen ? "cd-test-note-info-wrap--active" : "",
            ]
                .filter(Boolean)
                .join(" ")}
        >
            {isOpen ? (
                <span className="cd-test-note-tooltip" role="tooltip">
                    {noteText}
                </span>
            ) : null}
            <button
                type="button"
                className="cd-test-note-info-btn"
                title="View note"
                aria-label="View note"
                aria-expanded={isOpen}
                onClick={handleToggle}
            >
                <i className="mdi mdi-information-outline" aria-hidden="true" />
            </button>
        </span>
    );
};

const getButtonIcon = (action) => {
    const text = `${action?.kind || ""} ${action?.label || ""} ${
        action?.routeKey || ""
    }`.toLowerCase();
    if (text.includes("upload")) return "fa fa-file";
    if (text.includes("change")) return "fas fa-random";
    if (text.includes("resend")) return "mdi mdi-email-send-outline";
    if (text.includes("send")) return "fa fa-paper-plane";
    if (text.includes("watch")) return "mdi mdi-open-in-new";
    if (text.includes("view")) return "mdi mdi-open-in-new";
    if (text.includes("edit")) return "mdi mdi-pencil-outline";
    if (action?.label?.includes("Approve")) return "mdi mdi-check-bold";
    if (action?.label?.includes("Reject")) return "mdi mdi-close-thick";

    return "mdi mdi-play-circle-outline";
};

function CallDetailsCandidateApplicationStatusTestsSection({
    callDetailsRouteParameterId,
    candidateTestActionBodyCandidateClientId,
    candidateTestActionBodyCandidateId,
    candidateApplicationStatusTestRows,
    loading = false,
    onCandidateApplicationStatusTestsShouldReload,
    onRequestOpenDriverDetailsModal,
}) {
    const [loadingKey, setLoadingKey] = useState(null);
    const [updateModal, setUpdateModal] = useState(null);
    const [roadTestModal, setRoadTestModal] = useState(null);
    const [screeningMailModal, setScreeningMailModal] = useState(null);
    const [dispatchModal, setDispatchModal] = useState(null);

    const rows = useMemo(
        () =>
            sortCandidateApplicationStatusTestsByServerSortOrder(
                candidateApplicationStatusTestRows
            ),
        [candidateApplicationStatusTestRows]
    );

    const handleAction = useCallback(
        async (row, action) => {
            const progressKey = getProgressKey(row, action);

            setLoadingKey(progressKey);

            try {
                const requestBody = buildCandidateTestActionRequestBody(row, action, {
                    candidateClientId:
                        candidateTestActionBodyCandidateClientId,
                    candidateId: candidateTestActionBodyCandidateId,
                });

                const response =
                    await invokeCandidateTestActionForCallDetails({
                        candidateId: callDetailsRouteParameterId,
                        routeKey: action?.routeKey,
                        routeArgument: action?.routeArgument,
                        requestBody,
                        httpMethod: action?.httpMethod,
                    });

                if (isSuccess(response)) {
                    if (isScreeningMailGetAction(action)) {
                        const screeningMailData = response?.data ?? {};
                        setScreeningMailModal({
                            subject:
                                String(screeningMailData?.subject ?? "").trim() ||
                                action?.label ||
                                "Screening mail",
                            htmlBody:
                                String(screeningMailData?.htmlBody ?? "").trim() ||
                                response?.message ||
                                "",
                            sendAction:
                                resolveScreeningMailSendAction(screeningMailData),
                            requestBody,
                        });
                    } else {
                        toast.success(
                            response?.message || "Updated successfully"
                        );
                        await onCandidateApplicationStatusTestsShouldReload?.();
                    }
                } else {
                    toast.error(response?.message || "Something went wrong");
                }
            } finally {
                setLoadingKey(null);
            }
        },
        [
            callDetailsRouteParameterId,
            candidateTestActionBodyCandidateClientId,
            candidateTestActionBodyCandidateId,
            onCandidateApplicationStatusTestsShouldReload,
        ]
    );

    const onActionClick = useCallback(
        async (row, action) => {
            const label = String(action?.label || "").trim();

            if (
                doesCandidateTestActionOpenVideoInterviewCompletedLink(
                    row,
                    action
                )
            ) {
                openUrlInNewTab(
                    resolveVideoInterviewCompletedLink(row, action)
                );
                return;
            }

            if (
                doesCandidateTestActionLabelOpenDriverDetailsDocumentModal(label)
            ) {
                onRequestOpenDriverDetailsModal?.();
                return;
            }

            if (
                doesCandidateTestRowAndLabelOpenRoadTestScheduleChangeModal(
                    row,
                    label
                )
            ) {
                setRoadTestModal({ row, action });
                return;
            }

            if (doesCandidateTestActionLabelOpenUpdateFormModal(label)) {
                setUpdateModal({ row, action });
                return;
            }

            if (
                action?.confirmMessage ||
                doesCandidateTestActionLabelRequireSendLinkConfirmation(label)
            ) {
                const result = await Swal.fire({
                    title: label || "Confirm",
                    text: action?.confirmMessage || "Are you sure?",
                    icon: "question",
                    showCancelButton: true,
                    confirmButtonText: "Yes",
                    cancelButtonText: "Cancel",
                    reverseButtons: true,
                });

                if (!result?.isConfirmed) return;
            }
            if(["view application", "View application", "View Application"].includes(label)){
                window.open(
                    `${action?.routeKey}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                  return
            }
            await handleAction(row, action);
        },
        [handleAction, onRequestOpenDriverDetailsModal]
    );

    if (loading) {
        return (
            <div className="application-status-list cd-status-cards">
                <div className="padding-lft cd-personal-loading py-4">
                    <Loaders />
                </div>
            </div>
        );
    }

    if (!rows.length) {
        return (
            <div className="application-status-list cd-status-cards">
                <div className="text-muted small py-2">
                    No application tests found.
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="application-status-list cd-status-cards">
                {rows.map((row) => {
                    const title =
                        resolveCandidateApplicationStatusTestTitle(row);

                    const badge =
                        resolveCandidateApplicationStatusTestBadgeLabel(row);

                    const badgeClassNames =
                        resolveCandidateApplicationStatusTestBadgeClassNames(row);

                    const fadvFileDispatch =
                        resolveCandidateTestFadvFileDispatch(row);

                    const dispatchChipClassNames = fadvFileDispatch
                        ? resolveCandidateTestFadvFileDispatchChipClassNames(
                              fadvFileDispatch
                          )
                        : "";

                    const dispatchStatusLabel = fadvFileDispatch
                        ? resolveCandidateTestFadvFileDispatchStatusLabel(
                              fadvFileDispatch
                          )
                        : "—";

                    const actions = row?.actions || [];

                    const showCompletedInterviewButton =
                        shouldRenderVideoInterviewCompletedViewButton(
                            row,
                            actions
                        );

                    const completedInterviewLink =
                        resolveVideoInterviewCompletedLink(row);

                    return (
                        <div
                            key={row?.candidateTestId || title}
                            className={resolveCandidateTestRowBackgroundClassNames(
                                row
                            )}
                        >
                            <div className="status-left">
                                <span className="status-title">
                                    {title}:
                                </span>

                                {badge && (
                                    <Badge className={badgeClassNames} pill>
                                        {badge}
                                    </Badge>
                                )}

                                {hasCandidateTestRowNote(row) ? (
                                    <CandidateTestRowNoteInfo note={row?.notes} />
                                ) : null}

                                {fadvFileDispatch ? (
                                    <button
                                        type="button"
                                        className={dispatchChipClassNames}
                                        title="View Uploaded File details"
                                        aria-label={`Dispatch ${dispatchStatusLabel}. View details`}
                                        onClick={() =>
                                            setDispatchModal({
                                                title,
                                                fadvFileDispatch,
                                            })
                                        }
                                    >
                                        <span
                                            className="cd-fadv-dispatch-chip__dot"
                                            aria-hidden="true"
                                        />
                                        {/* <span className="cd-fadv-dispatch-chip__label">
                                            Uploaded
                                        </span>
                                        <span
                                            className="cd-fadv-dispatch-chip__divider"
                                            aria-hidden="true"
                                        /> */}
                                        <span className="cd-fadv-dispatch-chip__status">
                                            {dispatchStatusLabel}
                                        </span>
                                    </button>
                                ) : null}
                            </div>

                            {(!!actions.length ||
                                showCompletedInterviewButton) && (
                                <div className="status-right">
                                    {showCompletedInterviewButton ? (
                                        <VideoInterviewCompletedViewButton
                                            href={completedInterviewLink}
                                        />
                                    ) : null}
                                    {actions.map((action) => {
                                        const progressKey =
                                            getProgressKey(row, action);

                                        const isLoading =
                                            loadingKey === progressKey;

                                        return (
                                            <Button
                                                key={progressKey}
                                                type="button"
                                                color={
                                                    action?.kind?.includes(
                                                        "mec"
                                                    )
                                                        ? undefined
                                                        : "primary"
                                                }
                                                className={getButtonClass(
                                                    action,
                                                    row?.wellKnownCategory
                                                )}
                                                disabled={isLoading}
                                                onClick={() =>
                                                    onActionClick(row, action)
                                                }
                                            >
                                                <i
                                                    className={
                                                        isLoading
                                                            ? "fas fa-spinner fa-spin me-1"
                                                            : `${getButtonIcon(
                                                                  action
                                                              )} me-1`
                                                    }
                                                />

                                                {action?.label || "Action"}
                                            </Button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <CallDetailsCandidateTestUpdateModal
                isOpen={!!updateModal}
                onRequestClose={() => setUpdateModal(null)}
                callDetailsRouteParameterId={callDetailsRouteParameterId}
                candidateTestActionBodyCandidateClientId={
                    candidateTestActionBodyCandidateClientId
                }
                candidateTestActionBodyCandidateId={
                    candidateTestActionBodyCandidateId
                }
                applicationStatusTestRow={updateModal?.row}
                actionDefinition={updateModal?.action}
                onSaveSuccess={
                    onCandidateApplicationStatusTestsShouldReload
                }
            />

            <CallDetailsRoadTestScheduleUpdateModal
                isOpen={!!roadTestModal}
                onRequestClose={() => setRoadTestModal(null)}
                callDetailsRouteParameterId={callDetailsRouteParameterId}
                candidateTestActionBodyCandidateClientId={
                    candidateTestActionBodyCandidateClientId
                }
                candidateTestActionBodyCandidateId={
                    candidateTestActionBodyCandidateId
                }
                applicationStatusTestRow={roadTestModal?.row}
                actionDefinition={roadTestModal?.action}
                onSaveSuccess={
                    onCandidateApplicationStatusTestsShouldReload
                }
            />

            <CallDetailsScreeningMailPreviewModal
                isOpen={!!screeningMailModal}
                onRequestClose={() => setScreeningMailModal(null)}
                subject={screeningMailModal?.subject}
                htmlBody={screeningMailModal?.htmlBody}
                sendAction={screeningMailModal?.sendAction}
                requestBody={screeningMailModal?.requestBody}
                callDetailsRouteParameterId={callDetailsRouteParameterId}
                onSendSuccess={onCandidateApplicationStatusTestsShouldReload}
            />

            <FadvFileDispatchDetailsModal
                isOpen={!!dispatchModal}
                onRequestClose={() => setDispatchModal(null)}
                requirementTitle={dispatchModal?.title}
                fadvFileDispatch={dispatchModal?.fadvFileDispatch}
            />
        </>
    );
}

export default CallDetailsCandidateApplicationStatusTestsSection;